import { Dropdown } from 'react-bootstrap';
import cn from 'classnames';
import { type IdType } from '../types/card';
import { useDropdown } from '../../contexts/DropdownContext';
import { useId } from 'react';
import { UseDropdownMenuOptions } from '@restart/ui/DropdownMenu';

export type SettingsDropdownData = {
  label: string | React.ReactNode;
  onClick: (id: IdType) => void;
  className?: string;
  style?: React.CSSProperties
};

type SettingsDropdownProps = {
  icon?: React.ReactNode;
  data?: SettingsDropdownData[];
  className?: string;
  align?: 'left' | 'right';
  bodyClassName?: string;
  id?: IdType;
  bodyStyle?: React.CSSProperties;
  fs?: number;
  fixed?: boolean;
  gapY?: number;
  gapX?: number;
};

const SettingsDropdown = ({
  icon,
  data,
  className,
  align = 'right',
  bodyClassName,
  id,
  bodyStyle,
  fs = 14,
  fixed = true, // Set to false if the element is initially hidden and cannot be rendered (display: none...)
  gapY = 5,
  gapX = 0
}: SettingsDropdownProps) => {
  const dropdownId = useId();
  const { activeDropdown, setActiveDropdown } = useDropdown();

  const popperConfig: UseDropdownMenuOptions['popperConfig'] = fixed ? {
    strategy: 'fixed',
    modifiers: gapY > 0 || gapX > 0 ? [{ name: 'offset', options: { offset: [gapX, gapY] } }] : []
  } : {
    modifiers: gapY > 0 || gapX > 0 ? [{ name: 'offset', options: { offset: [gapX, gapY] } }] : []
  };

  const handleToggle = (isOpen: boolean) => {
    if (isOpen) {
      setActiveDropdown(dropdownId);
    } else {
      setActiveDropdown(null);
    }
  };

  const defaultData: SettingsDropdownData[] = [
    {
      label: 'Clone',
      onClick: (id: IdType) => {
        console.log(id);
      }
    },
    {
      label: 'Edit',
      onClick: (id: IdType) => {
        console.log(id);
      }
    },
    {
      label: 'Publish',
      onClick: (id: IdType) => {
        console.log(id);
      }
    },
    {
      label: 'Delete',
      onClick: (id: IdType) => {
        console.log(id);
      },
      className: 'text-primary'
    }
  ];

  const generateSettingsDropdownIcon = () => {
    if (icon) return icon;
    return (
      <>
        <svg
          width='9'
          height='5'
          viewBox='0 0 9 5'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M4.5 5L0.602886 0.5H8.39711L4.5 5Z'
            fill='white'
          />
        </svg>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M1.27996 10.9406L2.11707 11.3258C2.28578 11.4032 2.41909 11.5419 2.49022 11.714C2.56134 11.8861 2.56502 12.0788 2.50052 12.2535L2.17287 13.1197C2.04956 13.4403 2.00753 13.7866 2.05058 14.1275C2.09364 14.4684 2.22042 14.7933 2.41951 15.0727C2.61333 15.3577 2.87362 15.5908 3.17768 15.7515C3.48174 15.9123 3.82033 15.9959 4.16393 15.995C4.42251 15.9957 4.67895 15.9479 4.92002 15.854L5.78233 15.5248C5.86395 15.4915 5.95173 15.4761 6.03976 15.4796C6.18021 15.4785 6.31792 15.5186 6.43595 15.5951C6.55398 15.6715 6.64717 15.781 6.70405 15.91L7.0875 16.7509C7.25595 17.1234 7.5276 17.4392 7.86998 17.6607C8.21236 17.8822 8.611 18 9.01824 18C9.42549 18 9.82412 17.8822 10.1665 17.6607C10.5089 17.4392 10.7805 17.1234 10.949 16.7509L11.3414 15.9046C11.3992 15.7763 11.4927 15.6674 11.6105 15.5911C11.7283 15.5148 11.8655 15.4742 12.0057 15.4742C12.0937 15.4719 12.1812 15.4873 12.2632 15.5194L13.1255 15.8594C13.3665 15.9548 13.6225 16.0062 13.8816 16.0113C14.2254 16.0049 14.5628 15.9167 14.866 15.7539C15.1693 15.591 15.4297 15.3582 15.626 15.0745C15.8218 14.7915 15.9465 14.465 15.9895 14.123C16.0324 13.7811 15.9923 13.4337 15.8726 13.1107L15.536 12.2445C15.4735 12.0704 15.4782 11.8791 15.5491 11.7084C15.6201 11.5377 15.7522 11.3999 15.9194 11.3222L16.7565 10.937C17.1273 10.7678 17.4417 10.4949 17.6622 10.151C17.8827 9.80706 18 9.40662 18 8.99753C18 8.58845 17.8827 8.18801 17.6622 7.84408C17.4417 7.50015 17.1273 7.22728 16.7565 7.05806L15.9194 6.67288C15.7503 6.59544 15.6167 6.45646 15.5455 6.28397C15.4743 6.11148 15.4709 5.9183 15.536 5.74338L15.8636 4.87717C15.9869 4.55662 16.029 4.21029 15.9859 3.86936C15.9428 3.52843 15.8161 3.20361 15.617 2.92414C15.4232 2.63914 15.1629 2.40611 14.8588 2.24536C14.5547 2.0846 14.2162 2.00101 13.8726 2.00187C13.614 2.00113 13.3575 2.04897 13.1165 2.14293L12.2542 2.47205C12.1725 2.5054 12.0848 2.52081 11.9967 2.51726C11.8584 2.51712 11.723 2.47698 11.6068 2.40161C11.4906 2.32625 11.3985 2.21886 11.3414 2.09229L10.949 1.2514C10.7824 0.877561 10.5114 0.560324 10.1688 0.338191C9.82626 0.116058 9.42688 -0.00142029 9.01914 1.29594e-05C8.60517 -0.000378124 8.1998 0.118723 7.85128 0.34314C7.50277 0.567558 7.22578 0.887841 7.05329 1.26587L6.66084 2.10314C6.60303 2.23144 6.50962 2.34028 6.3918 2.41662C6.27399 2.49295 6.13676 2.53354 5.99656 2.53353C5.91162 2.53465 5.82727 2.5193 5.74813 2.48832L4.88582 2.1592C4.44227 1.98813 3.95428 1.97218 3.5006 2.11392C3.04692 2.25566 2.65402 2.54682 2.3853 2.94041C2.18998 3.22163 2.0655 3.54625 2.02254 3.88642C1.97959 4.22659 2.01943 4.57215 2.13867 4.89345L2.46631 5.75966C2.52882 5.9337 2.52412 6.12496 2.45313 6.2957C2.38215 6.46643 2.25003 6.60423 2.08286 6.68192L1.24576 7.0671C0.87472 7.23393 0.559669 7.50506 0.338619 7.84776C0.117569 8.19046 -3.38273e-05 8.59009 7.29864e-09 8.99844C0.00352482 9.41183 0.126315 9.81533 0.353494 10.16C0.580673 10.5048 0.902492 10.7759 1.27996 10.9406ZM1.85063 8.33296L2.68774 7.94778C3.17672 7.72348 3.56277 7.32117 3.76793 6.82206C3.97309 6.32296 3.98208 5.76424 3.79308 5.25874L3.46544 4.39253C3.42243 4.28335 3.40731 4.16509 3.42146 4.04854C3.43561 3.93198 3.47858 3.82084 3.54645 3.72525C3.63691 3.58702 3.77242 3.48471 3.92976 3.43588C4.0871 3.38705 4.25644 3.39473 4.40876 3.45761L5.27107 3.78673C5.50897 3.87862 5.76148 3.92641 6.01636 3.92778C6.4241 3.925 6.82252 3.80499 7.16451 3.58195C7.5065 3.35891 7.77775 3.04216 7.94621 2.66916L8.32965 1.82827C8.38782 1.69914 8.48248 1.59003 8.60185 1.51447C8.72123 1.43892 8.86008 1.40027 9.00114 1.4033C9.14158 1.40215 9.2793 1.44228 9.39733 1.51875C9.51536 1.59522 9.60855 1.7047 9.66542 1.8337L10.0417 2.66916C10.2092 3.04379 10.4803 3.36223 10.8227 3.58663C11.1651 3.81103 11.5645 3.93195 11.9733 3.93502C12.2284 3.93539 12.4812 3.88754 12.7186 3.79396L13.5809 3.46484C13.734 3.40889 13.9012 3.40476 14.0569 3.45308C14.2125 3.5014 14.3482 3.59951 14.4432 3.73248C14.5113 3.82801 14.5543 3.93916 14.5685 4.05574C14.5826 4.17233 14.5674 4.29062 14.5242 4.39977L14.1966 5.26597C14.0127 5.7708 14.0243 6.32658 14.229 6.82324C14.4337 7.3199 14.8166 7.72124 15.3019 7.94778L16.139 8.33296C16.2662 8.39078 16.3738 8.48446 16.4489 8.6026C16.524 8.72074 16.5634 8.85826 16.5621 8.99844C16.5632 9.13952 16.5233 9.27785 16.4472 9.39642C16.371 9.51498 16.2621 9.60859 16.1336 9.66572L15.3019 10.0527C14.813 10.277 14.4269 10.6793 14.2217 11.1784C14.0166 11.6775 14.0076 12.2362 14.1966 12.7418L14.5242 13.608C14.5672 13.7171 14.5824 13.8354 14.5682 13.952C14.5541 14.0685 14.5111 14.1797 14.4432 14.2752C14.3528 14.4135 14.2173 14.5158 14.0599 14.5646C13.9026 14.6134 13.7332 14.6058 13.5809 14.5429L12.7186 14.2138C12.4807 14.1219 12.2282 14.0741 11.9733 14.0727C11.5657 14.0748 11.1671 14.1941 10.8248 14.4165C10.4825 14.6389 10.2108 14.9551 10.0417 15.3277L9.65822 16.1686C9.60104 16.2955 9.50862 16.403 9.39206 16.4784C9.27551 16.5538 9.13976 16.5938 9.00114 16.5936C8.86069 16.5947 8.72298 16.5546 8.60495 16.4781C8.48692 16.4017 8.39373 16.2922 8.33686 16.1632L7.94621 15.3277C7.77657 14.9558 7.50459 14.6404 7.16233 14.4186C6.82007 14.1969 6.42182 14.0781 6.01456 14.0763C5.75952 14.076 5.50669 14.1238 5.26927 14.2174L4.40696 14.5465C4.25383 14.6025 4.0867 14.6066 3.93102 14.5583C3.77535 14.5099 3.63966 14.4118 3.54465 14.2789C3.47663 14.1833 3.43357 14.0722 3.41941 13.9556C3.40526 13.839 3.42046 13.7207 3.46364 13.6116L3.79128 12.7454C3.97721 12.2396 3.96672 11.6821 3.76188 11.1838C3.55705 10.6855 3.17291 10.283 2.68594 10.0563L1.85063 9.66753C1.7216 9.61055 1.61205 9.51677 1.53556 9.39783C1.45908 9.2789 1.41903 9.14003 1.42038 8.99844C1.41974 8.85746 1.46012 8.71937 1.53656 8.60115C1.613 8.48292 1.72216 8.3897 1.85063 8.33296Z'
            fill='white'
          />
          <path
            d='M8.98599 12.0383C9.58444 12.0379 10.1693 11.8593 10.6668 11.5251C11.1642 11.1908 11.5518 10.716 11.7805 10.1605C12.0093 9.60496 12.069 8.99381 11.952 8.40425C11.835 7.81469 11.5467 7.27321 11.1234 6.84826C10.7001 6.42331 10.1609 6.13397 9.57391 6.01682C8.98693 5.89967 8.37856 5.95998 7.82571 6.19011C7.27285 6.42024 6.80033 6.80987 6.46789 7.30974C6.13545 7.8096 5.95801 8.39725 5.95801 8.9984C5.96086 9.80406 6.28085 10.5759 6.84814 11.1454C7.41543 11.7149 8.18396 12.0359 8.98599 12.0383ZM8.98599 7.36364C9.30644 7.36364 9.61969 7.4591 9.88613 7.63793C10.1526 7.81677 10.3602 8.07095 10.4829 8.36834C10.6055 8.66573 10.6376 8.99298 10.5751 9.30869C10.5125 9.6244 10.3582 9.91439 10.1316 10.142C9.90506 10.3696 9.61637 10.5246 9.30208 10.5874C8.98779 10.6502 8.66202 10.618 8.36597 10.4948C8.06991 10.3716 7.81687 10.163 7.63884 9.89538C7.46081 9.62773 7.36579 9.31307 7.36579 8.99117C7.37142 8.56195 7.54415 8.15201 7.84699 7.84916C8.14982 7.54631 8.55868 7.37462 8.98599 7.37088V7.36364Z'
            fill='white'
          />
        </svg>
      </>
    );
  };

  const generateData = () => {
    if (data) return data;
    return defaultData;
  };

  return (
      <Dropdown
        className={cn('settings-dropdown', className)}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onToggle={handleToggle}
        show={activeDropdown === dropdownId}
      >
        <Dropdown.Toggle
          variant='transparent'
          className='rounded-pill p-0 border-0'
          bsPrefix='dropdown-toggle d-flex align-items-center'
        >
          {generateSettingsDropdownIcon()}
        </Dropdown.Toggle>
        <Dropdown.Menu
          align={align === 'left' ? 'start' : 'end'}
          className={bodyClassName}
          style={bodyStyle}
          popperConfig={popperConfig}
          renderOnMount={fixed}
        >
          {generateData().map((item, index) => (
            <Dropdown.Item
              key={index}
              className={item.className}
              onClick={() => item.onClick(id!)}
              style={{ fontSize: `${fs}px`, ...item.style }}
            >
              {item.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
  );
};
export default SettingsDropdown;
